import React from "react";
import BaseModal from "react-modal";
import type { Props as BaseModalProps } from "react-modal";
import { Button } from "../Button/Button";
import clsx from "clsx";
import styles from "./Drawer.module.scss";
import { Label } from "../types/labels";
import { isPresent } from "../utils/checks";

export type DrawerProps = {
  title?: string;
  dataContentName: string;
  className?: string;
  labels?: {
    close: Label;
  };
  onRequestClose?: BaseModalProps["onRequestClose"];
  isOpen: BaseModalProps["isOpen"];
  appElement?: BaseModalProps["appElement"];
  shouldCloseOnOverlayClick?: BaseModalProps["shouldCloseOnOverlayClick"];
  shouldCloseOnEsc?: BaseModalProps["shouldCloseOnEsc"];
};

export const Drawer: React.FC<React.PropsWithChildren<DrawerProps>> = ({
  children,
  dataContentName,
  title,
  className,
  onRequestClose,
  shouldCloseOnOverlayClick,
  shouldCloseOnEsc,
  isOpen,
  appElement,
  labels = {
    close: "Close"
  }
}) => {
  return (
    <BaseModal
      appElement={appElement}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      isOpen={isOpen}
      data={{ "content-name": dataContentName }}
      contentLabel="modal"
      closeTimeoutMS={300}
      overlayClassName={{
        base: styles.overlay,
        afterOpen: styles.overlayAfterOpen,
        beforeClose: styles.overlayBeforeClose
      }}
      shouldFocusAfterRender={true}
      shouldReturnFocusAfterClose={true}
      preventScroll={true}
      style={{
        overlay: {},
        content: overrideModalStyles
      }}
      className={{
        base: clsx(styles.content, className),
        afterOpen: styles.contentAfterOpen,
        beforeClose: styles.contentBeforeClose
      }}
    >
      {(isPresent(title) || isPresent(onRequestClose)) && (
        <header className={styles.header}>
          <div className={styles.title}>{title}</div>
          {onRequestClose && (
            <Button
              renderAs="button"
              variant="text"
              size="regular"
              iconName="close"
              dataContentPiece="close-drawer"
              onClick={onRequestClose}
            >
              {labels.close}
            </Button>
          )}
        </header>
      )}
      <article className={styles.bodyContainer}>{children}</article>
    </BaseModal>
  );
};

// overrides default styles for react-modal
const overrideModalStyles = {
  position: undefined,
  top: undefined,
  right: undefined,
  left: undefined,
  bottom: undefined,
  border: undefined,
  background: undefined,
  overflow: undefined,
  WebkitOverflowScrolling: undefined,
  borderRadius: undefined,
  outline: undefined,
  padding: undefined
} satisfies React.CSSProperties;
